import { extractApiError, type HttpApiClient } from "@vana/core";
import { fetchMaxAttempts } from "../../../../shared/delivery/max-attempts";
import {
  type Ambassador,
  type EscalateParams,
  type ReprogramParams,
  type RescheduleItem,
} from "../../domain/entities/reschedule";
import {
  EscalateError,
  GetMaxAttemptsError,
  ListAmbassadorsError,
  ListReschedulesError,
  ReprogramError,
} from "../../domain/errors/errors";
import { type ReschedulesRepository } from "../../domain/repos/reschedules-repo";
import {
  type AmbassadorsResponse,
  type ReschedulesResponse,
} from "../../infrastructure/responses/reschedules-responses";

export class ReschedulesRepo implements ReschedulesRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listQueue(supervisorId: string): Promise<RescheduleItem[]> {
    try {
      const query = supervisorId ? `?supervisor_id=${encodeURIComponent(supervisorId)}` : "";
      const response = await this.apiClient.get<ReschedulesResponse>(
        `v1/supervisor/reschedules${query}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListReschedulesError(message, apiError);
    }
  }

  async listAmbassadors(): Promise<Ambassador[]> {
    try {
      const response = await this.apiClient.get<AmbassadorsResponse>("v1/users?role=ambassador");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListAmbassadorsError(message, apiError);
    }
  }

  async reprogram(params: ReprogramParams): Promise<void> {
    try {
      // Reschedule (not_delivered → assigned) to the new slot.
      await this.apiClient.post(`v1/requests/${params.requestId}/reschedule`, {
        deliveryDate: params.deliveryDate,
        deliveryShift: params.deliveryShift,
        reason: params.reason,
      });
      // Optionally reassign to a different ambassador (now that it is `assigned`).
      if (params.newAmbassadorId && params.newAmbassadorId !== params.currentAmbassadorId) {
        await this.apiClient.post(`v1/requests/${params.requestId}/reassignment`, {
          newAmbassadorId: params.newAmbassadorId,
          reason: params.reason,
        });
      }
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReprogramError(message, apiError);
    }
  }

  async escalate(params: EscalateParams): Promise<void> {
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/undeliverable`, {
        reason: params.reason,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new EscalateError(message, apiError);
    }
  }

  async getMaxAttempts(): Promise<number> {
    try {
      return await fetchMaxAttempts(this.apiClient);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetMaxAttemptsError(message, apiError);
    }
  }
}
